import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import './Analytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Analytics = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const [overview, setOverview] = useState({});
  const [dailyStats, setDailyStats] = useState([]);
  const [locations, setLocations] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30);

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email read:files read:folders',
        },
      });

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Fetch all analytics data in parallel
      const [overviewRes, dailyStatsRes, locationsRes, userActivityRes] = await Promise.all([
        api.get('/api/analytics/overview', { headers }),
        api.get(`/api/analytics/daily-stats?days=${timeRange}`, { headers }),
        api.get('/api/analytics/locations', { headers }),
        api.get('/api/analytics/user-activity', { headers })
      ]);

      setOverview(overviewRes.data);
      setDailyStats(dailyStatsRes.data);
      setLocations(locationsRes.data);
      setUserActivity(userActivityRes.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data. Admin access required.');
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const dailyChartData = {
    labels: dailyStats.map(stat => new Date(stat.date).toLocaleDateString()).reverse(),
    datasets: [
      {
        label: 'Unique Visitors',
        data: dailyStats.map(stat => stat.unique_visitors).reverse(),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
      },
      {
        label: 'New Visitors',
        data: dailyStats.map(stat => stat.new_visitors || 0).reverse(),
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.2)',
        tension: 0.1,
      },
      {
        label: 'Page Views',
        data: dailyStats.map(stat => stat.page_views).reverse(),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.1,
      },
    ],
  };

  const topPagesData = {
    labels: overview.topPages?.map(page => page.page_name || page.page_path || 'Home') || [],
    datasets: [
      {
        data: overview.topPages?.map(page => page.views) || [],
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
          '#FF6384',
          '#C9CBCF',
          '#4BC0C0',
          '#FF6384',
        ],
      },
    ],
  };

  const locationData = {
    labels: locations.length > 0 ? locations.map(loc => `${loc.city}, ${loc.country}`) : ['No Data'],
    datasets: [
      {
        label: 'Unique Visitors',
        data: locations.length > 0 ? locations.map(loc => loc.unique_visitors) : [0],
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading">Loading analytics data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1>Site Analytics Dashboard</h1>
        <p>Admin access - {user?.email}</p>
      </div>

      {/* Overview Cards */}
      <div className="overview-cards">
        <div className="card">
          <h3>Today's Visitors</h3>
          <div className="metric">{overview.dailyVisitors || 0}</div>
          <div className="sub-metric">New: {overview.dailyNewVisitors || 0}</div>
        </div>
        <div className="card">
          <h3>Weekly Visitors</h3>
          <div className="metric">{overview.weeklyVisitors || 0}</div>
          <div className="sub-metric">New: {overview.weeklyNewVisitors || 0}</div>
        </div>
        <div className="card">
          <h3>Monthly Visitors</h3>
          <div className="metric">{overview.monthlyVisitors || 0}</div>
          <div className="sub-metric">New: {overview.monthlyNewVisitors || 0}</div>
        </div>
        <div className="card">
          <h3>Today's Page Views</h3>
          <div className="metric">{overview.dailyPageViews || 0}</div>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="controls">
        <label>
          Time Range:
          <select value={timeRange} onChange={(e) => setTimeRange(parseInt(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </label>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        <div className="chart-container">
          <h3>Daily Traffic Trends</h3>
          <Line data={dailyChartData} options={{ responsive: true, maintainAspectRatio: false }} />
        </div>

        <div className="chart-container">
          <h3>Top Pages</h3>
          <Doughnut data={topPagesData} options={{ responsive: true, maintainAspectRatio: false }} />
        </div>

        <div className="chart-container">
          <h3>Visitor Locations</h3>
          <Bar data={locationData} options={{ responsive: true, maintainAspectRatio: false }} />
        </div>
      </div>

      {/* Data Tables */}
      <div className="tables-grid">
        <div className="table-container">
          <h3>Top Locations</h3>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Location</th>
                <th>Unique Visitors</th>
                <th>Total Visits</th>
              </tr>
            </thead>
            <tbody>
              {locations.length > 0 ? (
                locations.slice(0, 10).map((location, index) => (
                  <tr key={index}>
                    <td>{location.city}, {location.country}</td>
                    <td>{location.unique_visitors}</td>
                    <td>{location.total_visits}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', color: '#999' }}>
                    No location data available yet. Visit from a public IP address to see location tracking.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="table-container">
          <h3>Most Active Users</h3>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>User Email</th>
                <th>Page Views</th>
                <th>Pages Visited</th>
                <th>Last Visit</th>
              </tr>
            </thead>
            <tbody>
              {userActivity.slice(0, 10).map((user, index) => (
                <tr key={index}>
                  <td>{user.user_email}</td>
                  <td>{user.page_views}</td>
                  <td>{user.pages_visited}</td>
                  <td>{new Date(user.last_visit).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;